import { $t } from '@/i18n';
import modalFactory from '@/utils/modalFactory';
import { defineComponent, ref, computed } from '@vue/composition-api';
import ExpireAt from '@/pages/modules/permission/expireAt';
import ManagePermModal from '@/pages/modules/permission/ManagePermModal';
import { providePermState } from '@/pages/modules/permission/PermState';
import { useRouter } from '@/router/useRouter';
import { permissionApi } from '@/services';
import { listMcpKeyRuntime, createMcpKeyRuntime } from '@/services/mcpKey';
import { keyBy } from 'lodash';
import { message } from 'ant-design-vue';
import { getRuntimePageBasePath } from '@/utils/path';
const columns = [
    {
        dataIndex: 'role',
        key: 'role',
        slots: {
            title: 'roleTitle',
        },
        scopedSlots: {
            customRender: 'role',
        },
    },
    {
        title: $t('k_d835c796'),
        dataIndex: 'expireAt',
        key: 'expireAt',
        scopedSlots: { customRender: 'expireTime' },
    },
    {
        title: $t('k_475e8e0f'),
        scopedSlots: {
            customRender: 'action',
        },
    },
];
// MCP Key 表格列定义
const mcpKeyColumns = [
    {
        title: 'MCP Key',
        dataIndex: 'mcpKey',
        key: 'mcpKey',
        scopedSlots: { customRender: 'mcpKey' },
    },
    {
        title: $t('k_9381362e'),
        dataIndex: 'status',
        key: 'status',
        scopedSlots: { customRender: 'status' },
    },
    {
        title: $t('k_acb9c6b9'),
        dataIndex: 'metadata',
        key: 'metadata',
    },
    {
        title: $t('k_242a4446'),
        dataIndex: '_ctime',
        key: '_ctime',
        scopedSlots: { customRender: 'createTime' },
    },
];
const userPermissionModal = defineComponent({
    name: 'userPermissionModal',
    components: {
        ExpireAt,
    },
    props: {
        options: {
            type: Object,
            default: () => ({}),
        },
    },
    setup(props, { emit }) {
        const showPermModal = ref(false);
        const permModalRole = ref({});
        const { router } = useRouter();
        providePermState();
        // 从 options 或 window.RUNTIME_CONFIGS 获取参数
        const projectId = props.options.projectId || window.RUNTIME_CONFIGS?.projectId;
        const env = props.options.env || window.RUNTIME_CONFIGS?.env;
        const roles = ref([]);
        const userRoles = ref([]);
        const fetchRoles = async () => {
            roles.value = await permissionApi.fetchRoles(projectId, env);
        };
        const fetchUserView = async () => {
            const resp = await permissionApi.fetchUserView(projectId, env);
            userRoles.value = resp.roles;
        };
        const openPermModal = (role) => {
            permModalRole.value = { ...role };
            showPermModal.value = true;
        };
        const jumpToPermissionApply = (role) => {
            emit('message', {});
            router.value.push({
                path: '/403',
                query: {
                    applying: 'true',
                    role,
                },
            });
        };
        const isSystemRole = (role) => {
            if (!role.startsWith('project')) {
                return false;
            }
            const [, roleId] = role.split('.');
            if (roleId && ['admin', 'guest'].includes(roleId)) {
                return true;
            }
        };
        const tableData = computed(() => {
            const rawData = userRoles.value;
            const roleMap = keyBy(roles.value, 'name');
            return rawData.map((role) => ({
                ...role,
                name: roleMap[role.role]?.name || role.role,
                cname: roleMap[role.role]?.cname ?? role.role,
                _id: role._id || role.role,
            }));
        });
        // Tab 相关状态
        const activeTab = ref('permission');
        const mcpKeyList = ref([]);
        const mcpKeyLoading = ref(false);
        const showKeyStates = ref({});
        // 申请 Key 对话框状态
        const showApplyDialog = ref(false);
        const applyForm = ref({
            metadata: '',
        });
        const applyLoading = ref(false);
        // 获取 MCP Key 列表
        const fetchMcpKeyList = async () => {
            mcpKeyLoading.value = true;
            try {
                const data = await listMcpKeyRuntime(projectId, env, {
                    size: 'total',
                    include: '',
                    filter: '',
                    sort: '_ctime',
                    order: 'desc',
                });
                mcpKeyList.value = data || [];
                // 初始化显示状态
                const states = {};
                (data || []).forEach((item) => {
                    states[item.mcpKey] = false;
                });
                showKeyStates.value = states;
            }
            catch (error) {
                message.error($t('k_2ecf2c41', { arg_0: error }));
            }
            finally {
                mcpKeyLoading.value = false;
            }
        };
        // 切换 Key 显示状态
        const toggleKeyVisibility = (id) => {
            showKeyStates.value = {
                ...showKeyStates.value,
                [id]: !showKeyStates.value[id],
            };
        };
        // 申请新 Key
        const handleApplyNewKey = () => {
            applyForm.value = {
                metadata: '',
            };
            showApplyDialog.value = true;
        };
        // 提交申请
        const handleApplySubmit = async () => {
            if (!applyForm.value.metadata) {
                message.error($t('k_b88b2726'));
                return;
            }
            applyLoading.value = true;
            try {
                const result = await createMcpKeyRuntime({
                    projectId,
                    env,
                    data: {
                        metadata: applyForm.value.metadata,
                    },
                });
                message.success($t('k_14af6211'));
                showApplyDialog.value = false;
                // 申请成功 1s 后跳转到审批流程详情页
                setTimeout(() => {
                    const { objectId, _id } = result || {};
                    if (objectId && _id) {
                        // 构造跳转 URL
                        const host = window.location.origin;
                        const basePath = getRuntimePageBasePath({ env, projectId, host: window.location.host });
                        const detailUrl = `${host}${basePath}/wuji/workflow/entity-detail?id=${objectId}&entityid=${_id}`;
                        // 在新标签页打开
                        window.open(detailUrl, '_blank');
                    }
                }, 1000);
                // 刷新列表
                fetchMcpKeyList();
            }
            catch (error) {
                message.error($t('k_2090abdf', { arg_0: error }));
            }
            finally {
                applyLoading.value = false;
            }
        };
        // 复制 Key
        const copyMcpKey = (key) => {
            navigator.clipboard.writeText(key).then(() => {
                message.success($t('k_8014ef31'));
            })
                .catch(() => {
                message.error($t('k_9aed9623'));
            });
        };
        return {
            permModalRole,
            showPermModal,
            openPermModal,
            jumpToPermissionApply,
            isSystemRole,
            roles,
            fetchRoles,
            fetchUserView,
            tableData,
            // Tab 相关
            activeTab,
            mcpKeyList,
            mcpKeyLoading,
            showKeyStates,
            fetchMcpKeyList,
            toggleKeyVisibility,
            // 申请 Key 相关
            showApplyDialog,
            applyForm,
            applyLoading,
            handleApplyNewKey,
            handleApplySubmit,
            copyMcpKey,
        };
    },
    created() {
        this.fetchRoles();
        this.fetchUserView();
    },
    methods: {
        handleTabChange(key) {
            this.activeTab = key;
            if (key === 'mcpKey' && this.mcpKeyList.length === 0) {
                this.fetchMcpKeyList();
            }
        },
    },
    render() {
        const tabPaneStyle = { padding: '12px 0' };
        return <a-modal visible={true} title={$t('k_b86ba482')} footer={null} onCancel={() => {
                this.$emit('message', {});
            }} width='50vw'>
      <a-tabs activeKey={this.activeTab} onChange={(key) => this.handleTabChange(key)}>
        <a-tab-pane tab={$t('k_294ecaac')} key="permission" style={tabPaneStyle}>
          <a-table columns={columns} dataSource={this.tableData} pagination={false} rowKey="_id" scopedSlots={{
                role: (role, record) => <div>
                  <a href="javascript:;" onClick={() => this.openPermModal(record)}>{record.cname || role} </a>
                  {record.userType === 'org' ? <a-tag>{$t('k_356013f2')}</a-tag> : null}
                </div>,
                expireTime: (expireAt) => <div>
                  {expireAt ? <ExpireAt expireAt={expireAt}></ExpireAt> : $t('k_eadabe83')}
                </div>,
                action: (_ref, record) => <div>
                  <a-button disabled={this.isSystemRole(record.role)} style='padding-left: 0' type="link" onClick={() => {
                        this.jumpToPermissionApply(record.role);
                    }}>{$t('k_1cbd7c30')}</a-button>
                </div>,
            }}>
            <div slot='roleTitle'>
              <span>{$t('k_697cd19b')} <a-popover>
                <div slot='content'>
                  {$t('k_2d74feb9')}
                </div>
                <a-icon type="question-circle"/>
                </a-popover>
              </span>
            </div>
          </a-table>
          <ManagePermModal showModal={this.showPermModal} roleOrPerm={this.permModalRole} onHide={() => {
                this.showPermModal = false;
            }}></ManagePermModal>
        </a-tab-pane>
        <a-tab-pane tab={$t('k_50c61f18')} key="mcpKey" style={tabPaneStyle}>
          <div style="margin-bottom: 16px; display: flex; align-items: center; gap: 8px;">
            <a-button type="primary" onClick={this.handleApplyNewKey}>
              {$t('k_c0e51e12')}
            </a-button>
            <a-tooltip title={$t('k_94158804')}>
              <a-icon type="question-circle" style="color: rgba(0, 0, 0, 0.45); cursor: help;"/>
            </a-tooltip>
          </div>
          <a-table columns={mcpKeyColumns} dataSource={this.mcpKeyList} loading={this.mcpKeyLoading} pagination={false} rowKey="_id" scopedSlots={{
                mcpKey: (mcpKey) => <div style="display: flex; align-items: center; gap: 8px;">
                <span style="font-family: monospace;">
                  {this.showKeyStates[mcpKey] ? mcpKey : '*'.repeat(mcpKey.length)}
                </span>
                <a-icon type="copy" style="cursor: pointer;" onClick={() => this.copyMcpKey(mcpKey)}/>
                <a-icon type={this.showKeyStates[mcpKey] ? 'eye-invisible' : 'eye'} style="cursor: pointer;" onClick={() => this.toggleKeyVisibility(mcpKey)}/>
              </div>,
                status: (status) => <a-tag color={status ? 'green' : 'red'}>
                {status ? $t('k_975011de') : $t('k_4c0c49a6')}
              </a-tag>,
                createTime: (_ctime) => new Date(_ctime).toLocaleString(),
            }}/>
        </a-tab-pane>
      </a-tabs>

      {/* 申请新 Key 对话框 */}
      <a-modal visible={this.showApplyDialog} title={$t('k_b29acb15')} onCancel={() => {
                this.showApplyDialog = false;
            }} onOk={this.handleApplySubmit} confirmLoading={this.applyLoading}>
        <a-form layout="vertical">
          <a-form-item label={$t('k_acb9c6b9')} required>
            <a-textarea value={this.applyForm.metadata} onInput={(e) => {
                this.applyForm.metadata = e.target.value;
            }} placeholder={$t('k_b5d96930')} rows={4}/>
          </a-form-item>
        </a-form>
      </a-modal>
    </a-modal>;
    },
});
export default modalFactory(userPermissionModal);
